/**
 * features/finance/AttendanceCheck.tsx
 * ------------------------------------------------------------------
 * "Is this child still coming to school?" — answered where the debt is, so
 * nobody has to walk to a classroom and read the paper register.
 *
 * Loads ON DEMAND, one pupil at a time. An arrears list runs to hundreds of
 * families; subscribing to every one of their registers to answer a question
 * asked about three of them would be the most expensive thing on the screen.
 *
 * Reads the class's registers by document-id range (`{classId}_{date}`), which
 * needs no composite index and fetches exactly the window asked for.
 * ------------------------------------------------------------------ */
import { useState } from 'react';
import { collection, documentId, getDocs, query, where } from 'firebase/firestore';
import { CalendarCheck, CircleAlert, CircleCheck, CircleHelp, Loader2, UserMinus } from 'lucide-react';
import { db } from '@/lib/firebase';
import {
  checkAttendance, registerIdRange, lookbackFrom,
  type AttendanceCheck as Check, type DailyRegister, type DayState,
} from '@/lib/attendanceCheck';

const COLLECTION = 'reportDailyAttendance';

const VERDICT = {
  attending: { icon: CircleCheck, cls: 'text-emerald-600 dark:text-emerald-400', label: 'Attending' },
  patchy: { icon: CircleAlert, cls: 'text-amber-600 dark:text-amber-400', label: 'In and out' },
  'not-seen': { icon: CircleAlert, cls: 'text-red-600 dark:text-red-400', label: 'Not seen' },
  'off-roll': { icon: UserMinus, cls: 'text-red-600 dark:text-red-400', label: 'Off the register' },
  unknown: { icon: CircleHelp, cls: 'text-gray-500', label: 'No register' },
} as const;

const DOT: Record<DayState, string> = {
  present: 'bg-emerald-500',
  late: 'bg-amber-400',
  excused: 'bg-sky-400',
  absent: 'bg-red-500',
  'off-roll': 'bg-gray-800 dark:bg-gray-200',
  'no-register': 'bg-gray-300 dark:bg-gray-600',
};

const DOT_TITLE: Record<DayState, string> = {
  present: 'Present',
  late: 'Late',
  excused: 'Absent — excused',
  absent: 'Absent',
  'off-roll': 'Class marked, pupil not on the register',
  'no-register': 'No register',
};

export default function AttendanceCheckPanel({
  studentId, classId, studentName, compact = false, studentStatus, leftDate,
}: {
  studentId: string;
  classId?: string;
  studentName?: string;
  compact?: boolean;
  /** The pupil's status on the school roll — 'left' where they have withdrawn. */
  studentStatus?: string;
  leftDate?: string;
}) {
  const [state, setState] = useState<'idle' | 'loading' | 'done' | 'error'>('idle');
  const [check, setCheck] = useState<Check | null>(null);
  const [error, setError] = useState('');

  async function load() {
    if (!classId) { setError('This pupil has no class on record, so there is no register to read.'); setState('error'); return; }
    setState('loading'); setError('');
    try {
      const today = new Date().toISOString().slice(0, 10);
      const [lo, hi] = registerIdRange(classId, lookbackFrom(today), `${today}`);
      const snap = await getDocs(query(
        collection(db, COLLECTION),
        where(documentId(), '>=', lo),
        where(documentId(), '<=', hi),
      ));
      const registers = snap.docs.map((d) => d.data() as DailyRegister);
      setCheck(checkAttendance(studentId, registers));
      setState('done');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not read the register.');
      setState('error');
    }
  }

  // TWO INDEPENDENT SIGNALS, shown as two. The roll is what the school RECORDED;
  // the register is what actually happened. They disagree more often than anyone
  // expects — a pupil marked 'left' who is still in class, or on the roll and
  // gone for a month — and collapsing them into one verdict would hide exactly
  // the case worth investigating.
  const hasLeft = String(studentStatus ?? '').toLowerCase() === 'left';
  const leftBanner = hasLeft ? (
    <p className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1.5 rounded-lg bg-red-50 text-red-700 border-2 border-red-200 dark:bg-red-950/40 dark:text-red-300 dark:border-red-900/60">
      <UserMinus size={14} className="shrink-0" />
      Marked as having left the school{leftDate ? ` on ${leftDate}` : ''}
    </p>
  ) : null;

  if (state === 'idle') {
    return (
      <div className="flex flex-wrap items-center gap-2">
        {leftBanner}
      <button
        type="button"
        onClick={load}
        className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1.5 rounded-lg border-2 border-sky-200 bg-sky-50 text-sky-700 hover:bg-sky-100 dark:border-sky-900/60 dark:bg-sky-950/40 dark:text-sky-300 dark:hover:bg-sky-950/70 transition-colors"
        title={`Check whether ${studentName ?? 'this pupil'} is still attending`}
      >
        <CalendarCheck size={14} /> Still attending?
      </button>
      </div>
    );
  }

  if (state === 'loading') {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs opacity-70">
        <Loader2 size={14} className="animate-spin" /> Reading the register...
      </span>
    );
  }

  if (state === 'error' || !check) {
    return <span className="text-xs text-red-600 dark:text-red-400">{error}</span>;
  }

  const v = VERDICT[check.verdict];
  const Icon = v.icon;
  // Newest last, so the strip reads left-to-right like a calendar.
  const strip = [...check.days].reverse().slice(-25);

  return (
    <div className={`rounded-lg border border-gray-100 dark:border-gray-700/70 bg-gray-50 dark:bg-gray-900/40 ${compact ? 'p-2.5' : 'p-3'}`}>
      {leftBanner && <div className="mb-2">{leftBanner}</div>}
      <div className="flex items-start gap-2">
        <Icon size={16} className={`${v.cls} shrink-0 mt-0.5`} />
        <div className="min-w-0">
          <p className={`text-xs font-bold ${v.cls}`}>{v.label}</p>
          <p className="text-xs opacity-75 mt-0.5">{check.summary}</p>
        </div>
      </div>

      {strip.length > 0 && (
        <div className="mt-2.5">
          <div className="flex flex-wrap gap-[3px]" aria-hidden="true">
            {strip.map((d) => (
              <span
                key={d.date}
                title={`${d.date} — ${DOT_TITLE[d.state]}`}
                className={`w-2.5 h-2.5 rounded-[2px] ${DOT[d.state]}`}
              />
            ))}
          </div>
          <p className="text-[10px] opacity-50 mt-1.5">
            Last {strip.length} school day{strip.length === 1 ? '' : 's'}, oldest first ·
            {' '}green present · amber late · blue excused · red absent · black off the register
          </p>
        </div>
      )}
    </div>
  );
}
